import React from 'react';
import helpers from '../../../../commons/helpers/helpers';
import { useAppDispatch, useAppSelector } from '../../../../commons/store/hooks';
import { openRecommissioningReportDialog } from './wagonRecommissioningReportDucks';
import { RecommissioningReport } from './recommissioningReportTypes';
import { WagonTemplate } from '../../../../commons/model/templates';
import { getOfflineState, selectSecurityContextRoles } from '../../../../commons/selectors/selectors';
import classNames from 'classnames';
import { LIBRARY_READ, RECOMMISSIONING_REPORT_ADD } from '../../../../commons/security/userRoles';
import { useSelector } from 'react-redux';

type Props = {
  wagonTemplate: WagonTemplate;
  reports: RecommissioningReport[];
};

const WagonRecommissioningReportList = ({ wagonTemplate, reports }: Props) => {
  const dispatch = useAppDispatch();
  const offline = useSelector(getOfflineState);
  const roles = useAppSelector(selectSecurityContextRoles);
  const canSeeCancelComment = roles.includes('RECOMMISSIONING_REPORT_ADD');
  const hasCancelComment = reports.find((r) => r.cancelComment);

  const cancelLastRecommissioningReport = (recommissioningReport: RecommissioningReport) => {
    dispatch(openRecommissioningReportDialog(wagonTemplate, recommissioningReport, 'CANCEL'));
  };

  const downloadReport = (recommissioningReport: RecommissioningReport) => {
    window.open(`/api/recommissioning-reports/${recommissioningReport.id}/file`);
  };

  const openDialogToUploadReportFile = (report: RecommissioningReport) => {
    dispatch(openRecommissioningReportDialog(wagonTemplate, report, 'EDIT_REPORT_FILE'));
  };

  if (reports.length === 0) {
    return <></>;
  }

  const sortedReports = [...reports].sort((a, b) => b.updatedDate.localeCompare(a.updatedDate));

  return (
    <div className="wagon-recommissioning-report-list">
      <label>Rapports ARES</label>
      <table className="wagon-recommissioning-report-table">
        <tbody>
          <tr>
            <th className="column-recommissioning-date">Date de remise de service</th>
            <th className="column-updated">Ajouté par</th>
            {canSeeCancelComment && hasCancelComment && (
              <th className="column-cancel-comment">Commentaire d'annulation</th>
            )}
            {!offline && <th className="column-actions">Action</th>}
          </tr>
          {sortedReports.map((report, index) => (
            <tr key={`report-${report.id}`} className={classNames(report.status === 'CANCELLED' ? 'cancelled' : '')}>
              <td className="column-recommissioning-date">
                {helpers.dateTimeFormat(report.recommissioningReportDate, 'D MMM YYYY')}
              </td>

              <td className="column-updated">
                <span>
                  {report.updatedBy}
                  <br />
                  {helpers.dateTimeFormat(report.updatedDate, 'D MMM YYYY à HH:mm')}
                </span>
              </td>

              {canSeeCancelComment && hasCancelComment && (
                <td className="column-cancel-comment">
                  <span>{report.cancelComment}</span>
                </td>
              )}

              {!offline && (
                <td className="column-actions">
                  <div className="action-group">
                    {report.s3Key && roles.includes(LIBRARY_READ) && (
                      <button
                        className="btn btn-icon download"
                        type="button"
                        onClick={() => downloadReport(report)}
                      ></button>
                    )}
                    {roles.includes(RECOMMISSIONING_REPORT_ADD) && report.status !== 'CANCELLED' && index === 0 && (
                      <>
                        {!report.s3Key && (
                          <button
                            className="btn btn-icon edit"
                            type="button"
                            onClick={() => openDialogToUploadReportFile(report)}
                          ></button>
                        )}
                        <button
                          className="btn btn-icon remove"
                          type="button"
                          onClick={() => cancelLastRecommissioningReport(report)}
                        ></button>
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WagonRecommissioningReportList;
